import React, { FC, memo, useMemo, useState, useEffect, useTransition } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import { HeatmapDataType } from '@demind-inc/core';
import { FluctuationSetByReasons, selectedDateAtom, useAuthContext } from '../../../../data-access';
import clsx from 'clsx';
import './CircadianChart.scss';
import { useRecoilValue } from 'recoil';
import { useGeneralSettings } from '../../../../hooks/useGeneralSettings';
import { CustomMarker } from './CustomMarker';
import { ChartLegend } from './ChartLegend';
import { EnergyFluctuations } from './EnergyFluctuations';
import { CHART_HEIGHT, grayLight, grayPurple, primaryColor, purpleTaro } from '../../constants';
import { CircadianChartToolTip } from './CircadianChartToolTip';
import { CircularProgress, Stack } from '@mui/material';
import { CircadianChartData, CircadianChartDecorator } from './types';
import {
  transformEventEmaFluctuationItem,
  transformPhaseEmaFluctuationItem,
  transformSleepFluctuationItem,
} from './EnergyFluctuations/helpers';
import { useTranslation } from 'react-i18next';

const XAXIS_TICK_NUM = 6;
const XAXIS_TICK_START_TIME = 5;
const CHUNK_SIZE = 500; // Process 500 items per frame
const SIMPLIFIED_CHART_HEIGHT = 286;

interface CircadianChartProps {
  selectedDateCircadianData: HeatmapDataType[];
  selectedDateEnergyFluctuations: FluctuationSetByReasons;
  avgCircadianData: HeatmapDataType[];
  hideLegend?: boolean;
  className?: string;
  isSimplified?: boolean;
}

const CircadianChart: FC<CircadianChartProps> = memo(
  ({
    selectedDateCircadianData,
    selectedDateEnergyFluctuations,
    avgCircadianData,
    className,
    isSimplified,
  }) => {
    const { t } = useTranslation();
    const {
      generalSettings: { timeFormat },
    } = useGeneralSettings();
    const { user } = useAuthContext();
    const selectedDate = useRecoilValue(selectedDateAtom);
    const [isPending, startTransition] = useTransition();
    const [formattedData, setFormattedData] = useState<CircadianChartData[] | null>(null);
    const is24HourFormat = timeFormat === '24h';
    const [hoveredDecorator, setHoveredDecorator] = useState<CircadianChartDecorator | null>(null);

    const targetDate = useMemo(() => selectedDate.format('YYYY-MM-DD'), [selectedDate]);

    const { shouldShowPastData, xAxisTicks, yAxisTicks } = useMemo(() => {
      const shouldShowPastData = !selectedDate.isAfter(dayjs(), 'day');
      const xAxisTicks = Array.from({ length: XAXIS_TICK_NUM }, (_, i) => {
        const targetTime = i * 4 + XAXIS_TICK_START_TIME;
        return targetTime < 24
          ? dayjs(`${targetDate} ${String(targetTime).padStart(2, '0')}:00`).valueOf()
          : dayjs(
              `${selectedDate.add(1, 'day').format('YYYY-MM-DD')} ${String(targetTime - 24).padStart(2, '0')}:00`
            ).valueOf();
      });
      const yAxisTicks = [0, 0.2, 0.4, 0.6, 0.8, 1];

      return { shouldShowPastData, xAxisTicks, yAxisTicks };
    }, [selectedDate, targetDate]);

    const formatYAxis = (value: number) => {
      return `${Math.round(value * 100)}`;
    };

    useEffect(() => {
      startTransition(() => {
        // Precompute avgCircadianData map
        const avgDataMap = new Map<string, number>();
        avgCircadianData.forEach((avg) => {
          avgDataMap.set(dayjs(avg.y).format('HH:mm'), avg.value);
        });

        // Chunk the computation
        const chunks = [];
        for (let i = 0; i < selectedDateCircadianData.length; i += CHUNK_SIZE) {
          chunks.push(selectedDateCircadianData.slice(i, i + CHUNK_SIZE));
        }

        let accumulatedData: typeof formattedData = [];
        const processChunk = (chunkIndex: number) => {
          if (chunkIndex >= chunks.length) {
            setFormattedData(accumulatedData);
            return;
          }

          const chunkData = chunks[chunkIndex].map((item) => {
            const timeStr = dayjs(item.y).format('HH:mm');
            return {
              time: dayjs(item.y).valueOf(),
              currentValue: shouldShowPastData ? item.value : null,
              avgValue: avgDataMap.get(timeStr),
            };
          });

          accumulatedData = [...accumulatedData, ...chunkData];
          requestAnimationFrame(() => processChunk(chunkIndex + 1));
        };

        processChunk(0);
      });
    }, [selectedDateCircadianData, avgCircadianData, shouldShowPastData]);

    const circadianDataMap = useMemo(() => {
      const map = new Map<string, number>();
      for (const d of selectedDateCircadianData) {
        const timeStr = dayjs(d.y).format('YYYY-MM-DD HH:mm');
        map.set(timeStr, d.value);
      }
      return map;
    }, [selectedDateCircadianData]);

    const decorators = useMemo(() => {
      const decorations: CircadianChartDecorator[] = [];

      selectedDateEnergyFluctuations?.phaseEma?.forEach((emaData) => {
        const endTime = dayjs(emaData.eventTime.endTime);
        const details = transformPhaseEmaFluctuationItem(emaData, is24HourFormat, t);
        decorations.push({
          time: endTime.valueOf(),
          type: 'phaseEma',
          score: emaData.fluctuateScore,
          y: circadianDataMap.get(endTime.format('YYYY-MM-DD HH:mm')),
          details: {
            title: details.title,
            eventTime: details.eventTime,
            fluctuateValue: details.fluctuateValue,
            fluctuateTime: details.fluctuateTime,
            reason: 'phaseEma',
          },
        });
      });

      selectedDateEnergyFluctuations?.event?.forEach((event) => {
        if (event.eventTime.endTime) {
          const endTime = dayjs(event.eventTime.endTime);
          const details = transformEventEmaFluctuationItem(event, is24HourFormat);
          decorations.push({
            time: endTime.valueOf(),
            type: 'event',
            score: event.fluctuateScore,
            category: event.eventCategory?.id,
            y: circadianDataMap.get(endTime.format('YYYY-MM-DD HH:mm')),
            details: {
              title: details.title,
              eventTime: details.eventTime,
              fluctuateValue: details.fluctuateValue,
              fluctuateTime: details.fluctuateTime,
              reason: 'event',
            },
          });
        }
      });

      selectedDateEnergyFluctuations?.sleep?.forEach((sleep) => {
        const endTime = dayjs(sleep.eventTime.endTime);
          const details = transformSleepFluctuationItem(
            sleep,
            user?.chronotype,
            is24HourFormat,
            t
          );
        decorations.push({
          time: endTime.valueOf(),
          type: 'sleep',
          score: sleep.fluctuateScore,
          y: circadianDataMap.get(endTime.format('YYYY-MM-DD HH:mm')),
          details: {
            title: details.title,
            eventTime: details.eventTime,
            fluctuateValue: details.fluctuateValue,
            fluctuateTime: details.fluctuateTime,
            reason: 'sleep',
          },
        });
      });

      return decorations;
    }, [selectedDateEnergyFluctuations, circadianDataMap, is24HourFormat, user?.chronotype, t]);

    useEffect(() => {
      setHoveredDecorator((current) =>
        current &&
        decorators.some(
          (decorator) => decorator.time === current.time && decorator.type === current.type
        )
          ? current
          : null
      );
    }, [decorators]);

    const formatXAxis = (timestamp: number) =>
      dayjs(timestamp).format(timeFormat === '24h' ? 'HH:mm' : 'h:mm a');

    if (!formattedData || isPending) {
      return (
        <Stack
          flex={1}
          justifyContent="center"
          alignItems="center"
          className="circadian-chart-container__loading"
        >
          <CircularProgress />
        </Stack>
      );
    }

    return (
      <div
        className={clsx('circadian-chart-container', className, { 'is-simplified': isSimplified })}
      >
        <ResponsiveContainer
          width="100%"
          height={isSimplified ? SIMPLIFIED_CHART_HEIGHT : CHART_HEIGHT}
        >
          <LineChart
            style={{ background: grayPurple, borderRadius: 16 }}
            data={formattedData}
            margin={{ top: 30, right: 20, bottom: 10, left: 10 }}
          >
            <CartesianGrid stroke={grayLight} strokeOpacity={0.1} />
            <XAxis
              dataKey="time"
              tickFormatter={formatXAxis}
              ticks={xAxisTicks}
              type="number"
              domain={[xAxisTicks[0], xAxisTicks[xAxisTicks.length - 1]]}
              style={{ fontSize: '12px', color: grayLight }}
              tickLine={false}
              axisLine={false}
              padding={{ left: 30, right: 30 }}
            />
            {isSimplified && (
              <YAxis
                dataKey="currentValue"
                tickFormatter={formatYAxis}
                ticks={yAxisTicks}
                type="number"
                domain={[0, 1]}
                style={{ fontSize: '12px', color: grayLight }}
                tickLine={false}
                axisLine={false}
                width={40}
              />
            )}
            <Tooltip
              content={({ active, payload, label }) => (
                <CircadianChartToolTip
                  active={active}
                  payload={payload}
                  label={label}
                  hoveredDecorator={hoveredDecorator}
                  decorators={decorators}
                />
              )}
              wrapperStyle={{ outline: 'none' }}
              cursor={{ strokeDasharray: '3 3' }}
            />
            <Line
              type="monotone"
              dataKey="currentValue"
              stroke={primaryColor}
              strokeWidth={3}
              dot={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="avgValue"
              stroke={primaryColor}
              strokeWidth={3}
              strokeDasharray="4 1"
              dot={false}
              connectNulls
            />
            {decorators.map((decorator, index) => (
              <ReferenceDot
                key={index}
                x={decorator.time}
                y={decorator.y}
                shape={
                  <CustomMarker
                    eventType={decorator.type}
                    score={decorator.score}
                    category={decorator.category}
                    onHover={() => setHoveredDecorator(decorator)}
                    onHoverEnd={() =>
                      setHoveredDecorator((current) =>
                        current &&
                        (current.time !== decorator.time ||
                          current.type !== decorator.type ||
                          current.category !== decorator.category)
                          ? current
                          : null
                      )
                    }
                  />
                }
              />
            ))}
            {shouldShowPastData && (
              <ReferenceLine x={dayjs().valueOf()} stroke={purpleTaro} strokeWidth={1} />
            )}
          </LineChart>
        </ResponsiveContainer>
        {!isSimplified && (
          <>
            <ChartLegend />
            <EnergyFluctuations selectedDateEnergyFluctuations={selectedDateEnergyFluctuations} />
          </>
        )}
      </div>
    );
  }
);

export default CircadianChart;
